import React, { useCallback, useEffect, useRef, useState } from "react";
import extractUrls from "extract-urls";
import moment from "moment";
import {
  Badge,
  Box,
  Button,
  CloseButton,
  Flex,
  IconButton,
  Image,
  Input,
  Spinner,
  Text,
  Textarea,
  VStack,
  useBreakpointValue,
  useColorModeValue,
  useMediaQuery,
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  ArrowForwardIcon,
  ArrowUpIcon,
  AttachmentIcon,
  ChatIcon,
  CloseIcon,
  SmallCloseIcon,
} from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { cqAPI, getBid } from "../../lib/Auth";
import { useAppContext } from "../../AppContext";
import { renderMessageBody, renderAssistantBody, WIDGET_BODY_ATTRS } from "./supportBodySanitize";

// The widget marks the links it generated, so the panel can tell them from the ones a
// message already contained.
const EXTERNAL_LINK_ATTR = ' data-external-link="true"';
import {
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_MB,
  MAX_ATTACHMENT_BYTES,
  ALLOWED_ATTACHMENT_TYPES,
  ATTACHMENT_ACCEPT,
  BLOB_LOADING,
  OPAQUE_TYPE,
  safeContentType,
  isImageAttachment,
  attachmentPayload,
  formatBytes,
  newClientMessageId,
  uploadAttachment,
  attachmentBlobUrl,
} from "./supportAttachmentPolicy";

/**
 * Where an attachment is actually fetched from — the support route, not the URL stored
 * on the message. The generic private-file endpoint admits any member of the practice in
 * any role; this one asks the question support asks: is this the ticket's own owner, and
 * is the file on a message they are allowed to see?
 */
/**
 * How an attachment is cached and fetched: by MESSAGE as well as url.
 *
 * A url is not an identity. /upload honours a caller-chosen name for a private object, so
 * one thread can carry two messages naming the same object with different recorded
 * identities — and a cache keyed by url alone hands both of them whichever bytes were
 * fetched first, while the server, asked only for a url, answers with whichever record it
 * finds. Keyed by message, each one is fetched and judged on its own.
 */
/**
 * How an attachment is cached and fetched: by MESSAGE and POSITION.
 *
 * The server never sends the object's own url. That value is the storage key, and the
 * generic private-file endpoint admits any member of the practice in any role — so
 * anyone holding it reads a staff-only support file straight past the rule the support
 * endpoint exists to enforce. What arrives is where the attachment sits on its message,
 * which the server resolves against the thread.
 */
const attachmentKeyFor = (message, attachment) => `${message?._id || ""}|${attachment.ref}`;

const attachmentEndpoint = (ticketId, ref, messageId) =>
  `/support/tickets/${ticketId}/attachment?ref=${encodeURIComponent(ref)}`
  + (messageId ? `&message=${encodeURIComponent(messageId)}` : "");

import {
  heldSendsByScope,
  recordSend,
  dropSend,
  settleSend,
  offerableSend,
  forgetOtherUsers,
  onHeldSendsChanged,
  retireStoredSends as retireHeldSends,
} from "./supportHeldSends";

// Public API: anything in the app can open (and prefill) the widget with this window event.
export const OPEN_SUPPORT_CHAT_EVENT = "viva:open-support-chat";

export const openSupportChat = (question) =>
  window.dispatchEvent(
    new CustomEvent(OPEN_SUPPORT_CHAT_EVENT, { detail: { question } })
  );

// The conversation routes whose composer ends in a Send button at the bottom-right, where
// the closed launcher sits (MainComponent). The list pages at the bare prefixes have none.
// Case-insensitive, like the router. Team, group chat (/team/group/:groupId) and Intercom
// have no side panel, so their composer reaches the right edge at every width...
const EDGE_COMPOSER_ROUTE = /^\/(team|intercom)\/[^/]+/i;
// ...while the Inbox and Tasks one only does without the user-info panel: InboxMessages
// leaves it out for a patient-side view (AppContext `isPatient`, which staff who are also
// patients have), UserInfo hides itself while AppUI's mobile flag is set (Chakra `md`, in
// em, so it holds past 768px with a larger browser font), and Inbox.css drops it at
// `max-width: 768px` — 768px included, which Chakra's `md` already counts as desktop.
const PANEL_COMPOSER_ROUTE = /^\/(inbox|tasks)\/[^/]+/i;
const PANEL_DROPPED_QUERY = "(max-width: 768px)";

// Partner accounts are white-labelled; the customer must only see their brand.
const DEFAULT_BRAND = "Viva";

/**
 * Text arriving from somewhere else, made safe to show a white-labelled customer.
 *
 * The PO assistant hands its question straight into this composer, and the only thing
 * keeping the vendor's name out of it was an instruction in a prompt. A model that
 * ignores it — or is talked into ignoring it — puts "Viva" in front of a customer whose
 * practice has never heard the name, and if they press Send it is stored as their own
 * words, which the customer serializer deliberately never rewrites.
 *
 * Only ever narrows: with no partner in play the brand IS the default and nothing
 * changes.
 */
const brandSafe = (text, brand) => {
  // Characters that draw as nothing are removed FIRST. A literal match sees
  // `Vi<U+FE0F>va` as two fragments and leaves it; the screen shows the vendor's name.
  // Format characters (zero-width spaces and joiners, directional marks) plus the
  // combining marks that render blank — the grapheme joiner and the variation
  // selectors — which are not in `\p{Cf}` and survive normalisation. This mirrors
  // helpers/supportAssistant.js on the API, which does the same for stored messages.
  const visible = String(text || "")
    .normalize("NFKC")
    .replace(/[\p{Cf}\u034F\uFE00-\uFE0F\u{E0100}-\u{E01EF}]/gu, "");
  // Callback, not a replacement string — the same rule scrubBrand follows on the API.
  // A partner name is an unrestricted string, and one containing `$&` or `$1` is read by
  // `String.replace` as a substitution pattern: `$& Dental` puts the matched text back,
  // so "Ask Viva support" becomes "Ask Viva Dental support" and the vendor's name is
  // reintroduced by the very call meant to remove it. Sent, it is stored as the
  // customer's own wording, which is never scrubbed again.
  return brand === DEFAULT_BRAND
    ? visible
    : visible.replace(new RegExp(DEFAULT_BRAND, "gi"), () => brand);
};
// A business that belongs to a partner whose brand we cannot resolve gets a
// neutral word. Falling back to "Viva" there would be the exact leak we prevent.
const NEUTRAL_BRAND = "Support";
const roleLabels = (brand) => ({
  assistant: `${brand} assistant`,
  support: `${brand} support`,
});

const STATUS_LABELS = {
  ai: "assistant",
  open: "With our team",
  waiting_on_customer: "Waiting on you",
  closed: "Closed",
};

// A bound on every widget write. The customer's composer is cleared at send, so the
// message in flight is the only copy; a request that never settles — a half-open
// connection, a response whose headers arrived and whose body stalls — runs neither the
// catch that puts it back nor the finally that re-enables Send. The widget simply stops
// working, with the customer's words inside it. Two minutes is past any plausible slow
// success, and a send that is retried after one carries its client id, so the server
// recognises a repeat as a replay rather than storing it twice.
const WRITE_TIMEOUT_MS = 120000;

// "Talk to a human" is sent ONCE. Asking twice is meaningful — the server reads it as
// the customer saying the first page was lost, so it reopens the ticket, marks it
// unread for the whole team and pages again — and the request carries no client id that
// would let the server tell a deliberate second ask from the transport repeating the
// first.
const SEND_ONCE = { attempts: 1 };

const GENERIC_LOAD_ERROR = "We couldn't load your conversations. Please try again.";
const GENERIC_SEND_ERROR = "Your message couldn't be sent. Please try again.";


// Minimal local copy of the linkifier in Inbox/Messages.js - the helpers there are
// module-private and that file belongs to another owner, so it can't be imported.


/**
 * Human-written text may carry links. Assistant text may NOT: only the `links`
 * the server validated against this user's navigation map are clickable, so a
 * hallucinated or prompt-injected URL in the prose cannot become an anchor.
 *
 * Both allow-lists live in ./supportBodySanitize, shared with the agent console so the
 * two surfaces cannot drift apart. See that file for why it is an allow-list and why it
 * carries no custom ALLOWED_URI_REGEXP.
 */
const sanitizedBody = (body, { linkify }) =>
  linkify
    ? renderMessageBody(body, { anchorAttrs: EXTERNAL_LINK_ATTR, attrs: WIDGET_BODY_ATTRS })
    : renderAssistantBody(body);

const GENERIC_UPLOAD_ERROR = "We couldn't upload that file. Please try again.";



/** Empty when the file is fine, otherwise a sentence naming what is wrong with it. */
const attachmentRejection = (file, room) => {
  const name = file.name || "That file";
  if (room <= 0) return `You can attach up to ${MAX_ATTACHMENTS} files to a message.`;
  if (!ALLOWED_ATTACHMENT_TYPES.includes(String(file.type || "").toLowerCase())) {
    return `${name} is not a file type we can accept.`;
  }
  if (file.size > MAX_ATTACHMENT_BYTES) return `${name} is larger than ${MAX_ATTACHMENT_MB}MB.`;
  return "";
};

/** Only the four fields the API stores; the local bookkeeping never leaves here. */

/** The last message the customer can see: when it was written, and which one it is. */
const lastShownMessage = (ticket) => {
  const messages = (ticket && ticket.messages) || [];
  return messages.length ? messages[messages.length - 1] : null;
};
const lastMessageAt = (ticket) => lastShownMessage(ticket)?.created ?? null;

// Stands in for "the thread being composed" before it has an id of its own.
const NEW_THREAD = "__new__";

// Matches MAX_MESSAGE_CHARS in controllers/support.js, which refuses anything longer.
const MAX_MESSAGE_CHARS = 20000;

const HELD_SEND_WAITING =
  "A message you sent here didn't go through. Clear the box below and reopen this conversation to get it back.";

// Shown when the thread proves a message we handed back had in fact been stored. The
// customer is looking at words we told them had failed; taking them out of the box
// silently would read as losing them.
const SEND_ARRIVED_AFTER_ALL =
  "That message did go through after all — it's in your conversation above.";

const summarize = (ticket) => {
  const messages = ticket.messages || [];
  const last = messages[messages.length - 1];
  return {
    _id: ticket._id,
    subject: ticket.subject,
    status: ticket.status,
    last_message_at: ticket.last_message_at,
    created: ticket.created,
    last_message_preview: (last?.body || "").replace(/<[^>]+>/g, " ").slice(0, 140),
    unread: false,
  };
};

function SupportWidget() {
  const { user, selectedBusiness, location, realtime, realtimeInitialized, isPatient } =
    useAppContext();
  const navigate = useNavigate();

  const bid = selectedBusiness?.bid;
  const uid = user?.id;
  // White-label: partner accounts must only ever see their own brand, and a
  // partner-owned business with no resolvable brand must not fall back to Viva.
  const brand =
    selectedBusiness?.brand || (selectedBusiness?.partner ? NEUTRAL_BRAND : DEFAULT_BRAND);
  const labels = roleLabels(brand);
  const visible = !!(
    user &&
    bid &&
    // A REAL (platform) superadmin, not a partner operator. `user.isSuperAdmin` is
    // `superadmin OR partner-*`, so testing it alone also hid a partner admin who is
    // ALSO staff at a practice (`admin:<bid>`) — a genuine customer whom the API happily
    // serves (`customerCanUseSupport` excludes only an actual superadmin role).
    //
    // `isPlatformSuperadmin` is the precise flag from login. The fallback inference
    // (`isSuperAdmin && !isPartnerAdmin`) covers a session from before the flag existed,
    // and is wrong in exactly one corner — a MIXED superadmin+partner account reads as a
    // partner operator and sees a widget whose every request the API refuses. The flag
    // closes that; the inference merely degrades to the old behaviour until re-login.
    !(user.isPlatformSuperadmin ?? (user.isSuperAdmin && !user.isPartnerAdmin)) &&
    // /locations returns `partner` as a boolean and `support_chat` as a sibling flag it
    // has already resolved against the partner's settings AND against whether this person
    // is staff HERE — the same `isStaff && supportAvailable` the API gate uses, so a real
    // superadmin (staff of no practice) is already false here. `user.isPatient` used to be
    // checked too, but it is global — true if they are a patient anywhere — so a
    // front-desk employee who is also a patient of the practice they work at lost their
    // own support chat.
    selectedBusiness?.support_chat === true
  );

  const [isOpen, setIsOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  // The optimistic bubble, and WHICH thread it belongs to. A send takes as long as the
  // assistant does; the customer can press Back and open another conversation while it
  // runs, and an unscoped bubble then appears under whichever thread they are looking
  // at — somebody else's words in the middle of a different conversation.
  const [pendingMessages, setPendingMessages] = useState([]);
  const [pendingFor, setPendingFor] = useState(null);
  // Sends that failed while the customer was looking at a different conversation,
  // keyed by the thread they were sent on. Scoping the failure handling to the thread
  // stopped one conversation's text being restored into another's composer — but on
  // its own it also meant the message was simply dropped: gone from the server, from
  // the composer, and from the optimistic bubble the `finally` clears. It waits here
  // until they come back to the thread it belongs to.
  // Which held-send belongs to this customer and this conversation. The registry
  // outlives the component, so the key has to say who as well as what.
  // The registry entry the composer is currently holding, if it came from there.
  // Sending supersedes it: either this IS that message going out again, or the customer
  // replaced it — both make the old record obsolete at the same moment.
  const composerHeldRef = useRef(null);
  // Through a ref, because the restorer is defined below and the subscription wants to
  // be set up once.
  const newestThreadReadRef = useRef(0);
  const restoreHeldSendRef = useRef(null);
  // Defined below with the send machinery; `applyThread` sits above it.
  const retireStoredSendsRef = useRef(null);

  const heldKey = useCallback(
    (threadKey) => `${user?.id || "anon"}:${bid || "nobid"}:${threadKey}`,
    [user?.id, bid]
  );
  // `openTicket` is memoised, so reading `draft`/`attachments` from its closure would
  // see whatever they were when it was created. These mirror the live values so the
  // restore above can ask whether the composer is genuinely free right now.
  const draftRef = useRef("");
  const attachmentsRef = useRef([]);
  const ticketsRef = useRef([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [composeNew, setComposeNew] = useState(false);
  const [error, setError] = useState("");
  const [keyboardInset, setKeyboardInset] = useState(0);
  // Files picked for the message being written: {_id, name, size, content_type,
  // uploading, url}. `url` arrives when the upload finishes.
  const [attachments, setAttachments] = useState([]);
  const [attachError, setAttachError] = useState("");
  // Private files cannot be loaded straight into an <img>: they need the bearer
  // token, so each one is fetched once and kept as an object URL.
  const [attachmentBlobs, setAttachmentBlobs] = useState({});

  const activeIdRef = useRef(null);
  const composeNewRef = useRef(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  // Mirrors attachmentBlobs so the loader can tell what is already in flight
  // without re-running every time a blob lands.
  const attachmentBlobsRef = useRef({});
  // Holds the send id of a failed attempt so a retry does not duplicate it.
  const retryRef = useRef(null);
  // Identifies the practice+user the in-flight requests belong to. A send can take
  // tens of seconds; without this, switching practice mid-flight would paint the
  // previous tenant's conversation under the new one.
  const tenantRef = useRef(`${uid || ""}:${bid || ""}`);
  // Read the ref, never the closure: these callbacks are memoised, so a value
  // captured from render scope would be the tenant they were CREATED under, not
  // the current one.
  const tenantToken = () => tenantRef.current;
  const isStale = (token) => tenantRef.current !== token;

  /**
   * Which conversation the customer last ASKED for.
   *
   * Separate from the tenant token, because these are different questions. The tenant
   * token asks whether this result belongs to the practice on screen at all; this asks
   * whether the customer still wants to be looking at it. A fetch takes as long as it
   * takes, and they can move on while it runs.
   *
   * Without it, a background refresh started on A — realtime fires one on every reply —
   * lands after they have pressed Back and opened B, and paints A over the top. The
   * composer is NOT repainted with it: `adoptComposer` runs in `openTicket`, so B's
   * draft and B's staged files stay in the box under A's thread, and Send posts them to
   * A. The customer watched their own words go to the wrong conversation.
   *
   * Bumped by every deliberate move between conversations. An async result that no
   * longer matches is discarded rather than applied.
   */
  const navRef = useRef(0);
  const navToken = () => navRef.current;
  const navMoved = (token) => navRef.current !== token;

  /**
   * Whether the customer has steered at all.
   *
   * Auto-open — "you have one conversation, here it is" — is an ARRIVAL courtesy. Gated
   * only on "no thread is open", it also fired after every deliberate Back: the customer
   * leaves their one conversation, a realtime nudge lands, and the widget shoves them
   * straight back into it. Once they have steered anywhere, the widget stops choosing
   * for them until the world resets (a tenant change).
   */
  const customerSteeredRef = useRef(false);

  // ...and, separately, which refresh of the LIST is on screen. Freshness rather than
  // place: the list is the same list whichever thread they have open, so it lands across
  // a navigation — it just must not go backwards. See loadTickets.
  const listIssuedRef = useRef(0);
  const listRenderedRef = useRef(0);
  // How many `openTicket` calls are still running. The spinner comes down when the last
  // of them does, whichever one that is — see openTicket.
  const openingRef = useRef(0);

  /**
   * ...and which snapshot of the OPEN THREAD is on screen.
   *
   * A third question again, and the one the other two do not answer. `navRef` says
   * whether the customer still wants this conversation; the list sequence orders the
   * list. Neither orders two reads of the SAME thread against each other — and there are
   * several: the realtime refresh, `openTicket`, and the escalate response. Two refreshes
   * of A can pass every other guard and still land out of order, and the older one wins,
   * so the newest reply drops off the screen until something else refetches.
   */
  const threadIssuedRef = useRef(0);
  const threadRenderedRef = useRef(0);
  const applyThread = useCallback((ticket, at, { fromRead = false } = {}) => {
    if (at < threadRenderedRef.current) return;
    // The newest READ, specifically. "Newer than what is on screen" and "the newest one
    // asked for" differ exactly when the newest read FAILS or is discarded: it paints
    // nothing, the rendered mark does not move, and an older read — issued before the
    // reply that caused the refresh — then passes and paints a thread without it.
    // Realtime fires once per event, so nothing comes back for it. A WRITE is exempt on
    // purpose: `applyWrite` numbers an answer when it arrives so it outranks every read
    // that could not have seen it.
    if (fromRead && at !== newestThreadReadRef.current) return;
    threadRenderedRef.current = at;
    retireStoredSendsRef.current?.(ticket);
    setActiveTicket(ticket);
  }, []);
  /**
   * A WRITE's answer — a send, an escalation — numbered when it ARRIVES rather than when
   * it was sent.
   *
   * Issue order is not commit order. A send takes as long as the assistant does, and a
   * refresh issued while it ran reads a thread that does not contain the message yet; if
   * the send were numbered on the way out, that refresh would outrank it and the
   * customer's own message would vanish from the thread it was just added to. Numbering
   * on the way back makes a write newer than every read that could not have seen it.
   */
  const applyWrite = useCallback(
    (ticket) => applyThread(ticket, (threadIssuedRef.current += 1)),
    [applyThread],
  );

  /**
   * Put a message that failed to send back in front of the customer.
   *
   * A send can take as long as the assistant does, and the composer stays usable while
   * it runs — so by the time it fails they may have typed something else. Restoring
   * only into an empty composer silently threw the failed message away: it had already
   * been cleared from the draft, its optimistic bubble is gone, and nothing in the UI
   * still holds it. Their words, definitely not stored, simply disappear.
   *
   * So it goes back above whatever they have since typed, in the order they wrote
   * them, and they can edit or send from there.
   */
  /**
   * Put a failed send back in front of the customer — whole, or not yet.
   *
   * It used to PREPEND the failed body to whatever had been typed since, which merges
   * two messages into one. That is the same thing `restoreHeldSend` refuses to do, and
   * for a worse reason here: the merged text no longer matches the retry identity, so
   * the resend mints a NEW client id — and if the original had in fact committed before
   * its response was lost, the customer ends up with their first message stored twice,
   * the second time with their next one glued onto it.
   *
   * So it is restored whole when the composer is free, and stays recorded when it is
   * not. The registry keeps it either way; the error line says which happened.
   */
  const restoreOrHold = (entry, failed, files) => {
    if (draftRef.current || attachmentsRef.current.length) {
      setError(HELD_SEND_WAITING);
      // It stays queued — and the retry identity goes with it, exactly as the console
      // does on this branch. What is in the composer now is a DIFFERENT message the
      // customer typed while this one was in flight; leaving the failed send's identity
      // live lets that draft inherit its `clientMessageId` when it happens to say the
      // same thing. A customer whose message seems stuck retypes it, and if the first
      // one did commit, the server recognises the id, stores nothing, and answers "ok" —
      // so their deliberate second message is swallowed without a word.
      retryRef.current = null;
      return;
    }
    // The mirrors eagerly, as `adoptComposer` does: what follows asks them whether the
    // composer is free, and the effect that copies state into them has not run yet.
    draftRef.current = failed;
    attachmentsRef.current = files;
    setDraft(failed);
    setAttachments(files);
    retryRef.current = {
      scope: entry.scope,
      body: entry.body,
      attachmentKey: entry.attachmentKey,
      clientMessageId: entry.clientMessageId,
    };
    composerHeldRef.current = entry;
  };

  /**
   * Give back a send that failed while the customer was looking somewhere else.
   *
   * Restored WHOLE or not at all. The composer — draft, attachments and the retry
   * record — is one shared thing across conversations, so merging a held message into
   * a composer that already has something in it mixes two threads: the body of one
   * with the files of the other, and then sends them somewhere neither was written
   * for. It stays held until the composer is free, and the error line says so.
   *
   * `clientMessageId` comes back with it. Without that a retry mints a new one, and a
   * send that DID commit before its response was lost gets stored a second time — the
   * customer's own message duplicated in their thread.
   */
  /**
   * The composer belongs to a conversation, not to the widget.
   *
   * `draft`, `attachments` and the retry record were one shared set of values across
   * every thread, so typing on A, pressing Back and opening B put A's words — and A's
   * files — in front of B's Send button. The customer sees their own draft where they
   * left it and has no reason to think it is now addressed somewhere else.
   *
   * Each thread's composer is parked here on the way out and picked up on the way in.
   * `NEW_THREAD` is a key like any other, so a half-written new conversation survives
   * a look at the list too.
   */
  const composersRef = useRef({});

  /**
   * Which conversation the composer on screen belongs to, or `null` when there is none.
   *
   * Derived exactly the way the render derives `showList`, and it has to be: this answers
   * "which composer is this?" and that must agree with "which composer is rendered?".
   *
   * The version that asked `composeNewRef` alone disagreed in the case that matters most.
   * A customer with NO conversations yet is shown the composer — the list needs more than
   * one thread to earn its place — but nothing had marked them as composing, so the key
   * was `null`. Their very first message then found `stillHere()` false: the ticket the
   * server had just created was never opened, the assistant's answer never appeared, and
   * the next Send opened a SECOND ticket rather than replying to the first. Realtime
   * happened to paper over it by refreshing into the auto-open path, which is not a thing
   * to rely on.
   */
  const currentThreadKey = () => {
    if (activeIdRef.current) return activeIdRef.current;
    const listShowing = !composeNewRef.current && ticketsRef.current.length >= 1;
    return listShowing ? null : NEW_THREAD;
  };

  const parkComposer = useCallback(() => {
    const key = currentThreadKey();
    if (!key) return;
    if (draftRef.current || attachmentsRef.current.length) {
      composersRef.current[key] = {
        draft: draftRef.current,
        attachments: attachmentsRef.current,
        retry: retryRef.current,
        // The registry entry these words came back from, if they did. It belongs to the
        // composer, not to the widget: left global, the NEXT conversation's send treated
        // it as its own predecessor and dropped it — discarding the durable copy of a
        // message still sitting unsent in another thread's parked composer.
        held: composerHeldRef.current,
      };
    } else {
      delete composersRef.current[key];
    }
    // Parked or discarded, it is no longer what this composer is holding.
    composerHeldRef.current = null;
  }, []);

  const adoptComposer = useCallback((key) => {
    const parked = composersRef.current[key];
    const adoptedDraft = parked ? parked.draft : "";
    const adoptedAttachments = parked ? parked.attachments : [];
    // The mirrors are written HERE rather than left to the effect below. `setDraft` only
    // schedules; the effect that copies it into `draftRef` does not run until after the
    // commit — and `restoreHeldSend` is called on the very next line and asks those refs
    // whether the composer is free. It was answering about the thread being LEFT: a held
    // message was refused because the PREVIOUS conversation had a draft in it, or worse,
    // written straight over the draft this adoption had just restored.
    draftRef.current = adoptedDraft;
    attachmentsRef.current = adoptedAttachments;
    setDraft(adoptedDraft);
    setAttachments(adoptedAttachments);
    retryRef.current = parked ? parked.retry : null;
    composerHeldRef.current = parked ? parked.held || null : null;
    setAttachError("");
  }, []);

  /**
   * Retire every token this thread proves the server already has.
   *
   * A token is spent the moment its message is stored — but the widget could only learn
   * that from a send's own answer, and the cases that matter are exactly the ones where
   * no answer arrives: a truncated response, a session that expired underneath it. The
   * message then sits in the thread while the client still believes it never sent, so it
   * keeps offering it back, and a customer who retypes those same words spends the same
   * token on a message the server refuses as a duplicate — theirs is swallowed without
   * a word. The thread itself is the authority; this asks it.
   */
  const retireStoredSends = useCallback((ticket) => {
    const stored = new Set(
      (ticket?.messages || []).map(m => m.client_message_id).filter(Boolean)
    );
    if (!stored.size) return;

    const spent = (record) => (record && stored.has(record.clientMessageId) ? null : record);
    retryRef.current = spent(retryRef.current);

    // The WORDS as well as the token. Retiring the identity on its own left the message
    // sitting in the composer with nothing left to recognise it by: the customer reads a
    // message they were told had failed, presses Send, and — the retry record having just
    // been cleared — it goes out under a FRESH id as a second copy of something already on
    // their thread. Clearing the token without clearing what it identified is what turned
    // a duplicate the server would have caught into one it cannot see.
    //
    // Only while it is still exactly what was handed back. An edited draft is the
    // customer's next message, and theirs to send.
    const keyOf = (files) => files.map(a => a.url).join("|");
    const held = composerHeldRef.current;
    if (held && stored.has(held.clientMessageId)) {
      if (draftRef.current === held.body && keyOf(attachmentsRef.current) === held.attachmentKey) {
        draftRef.current = "";
        attachmentsRef.current = [];
        setDraft("");
        setAttachments([]);
        setError(SEND_ARRIVED_AFTER_ALL);
      }
      composerHeldRef.current = null;
    }

    for (const key of Object.keys(composersRef.current)) {
      const parked = composersRef.current[key];
      // A parked composer holds the same danger a live one does — it is simply waiting to
      // be adopted into the box. Emptied when it is holding nothing but a message the
      // thread already has, so adoption cannot resurrect the duplicate.
      if (parked.held && stored.has(parked.held.clientMessageId)
        && parked.draft === parked.held.body && keyOf(parked.attachments) === parked.held.attachmentKey) {
        delete composersRef.current[key];
      } else if ((parked.retry && spent(parked.retry) === null)
        || (parked.held && stored.has(parked.held.clientMessageId))) {
        composersRef.current[key] = {
          ...parked,
          retry: spent(parked.retry),
          held: parked.held && stored.has(parked.held.clientMessageId) ? null : parked.held,
        };
      }
    }

    // And the held records themselves: a message that is on the thread is not one the
    // customer is still owed a chance to send. Through the registry, which announces the
    // change — so whatever is now at the front of a queue is offered back instead of being
    // stranded behind the entry that just left.
    retireHeldSends(stored);
  }, []);

  const restoreHeldSend = useCallback((threadKey) => {
    // The oldest whose request has FINISHED. One still in flight can still commit, and
    // handing it back invites an edit that would arrive alongside it.
    const held = offerableSend(heldKey(threadKey));
    if (!held) return;

    if (draftRef.current || attachmentsRef.current.length) {
      setError(HELD_SEND_WAITING);
      return;
    }

    // Deliberately still recorded. Restoring is not confirmation: this composer dies
    // with the page too, and the server has not taken the message. It leaves the books
    // when the server confirms it, or when the customer sends something in its place.
    composerHeldRef.current = held;
    retryRef.current = {
      scope: held.scope,
      body: held.body,
      attachmentKey: held.attachmentKey,
      clientMessageId: held.clientMessageId,
    };
    draftRef.current = held.body;
    attachmentsRef.current = held.attachments;
    setDraft(held.body);
    setAttachments(held.attachments);
    setError(GENERIC_SEND_ERROR);
  }, [heldKey]);
  restoreHeldSendRef.current = restoreHeldSend;
  retireStoredSendsRef.current = retireStoredSends;

  // A send that settles for a thread the customer is no longer on has nothing to put it
  // back: its own branch returns as stale. This is how the message reaches whatever
  // screen they ARE on, the moment they return to its conversation.
  useEffect(() => onHeldSendsChanged(() => {
    const key = currentThreadKey();
    if (key !== null) restoreHeldSendRef.current?.(key);
  }), []);

  // A write in flight is the one moment leaving costs the customer something nothing
  // client-side can put back. The composer is cleared at Send, so the request holds the
  // only copy — and the held-send registry that recovers it is module memory, which dies
  // with the tab by design. The console already takes this hold; the widget did not, so a
  // customer who closed or reloaded while their message was still going lost it outright,
  // having already pressed Send.
  useEffect(() => {
    if (!sending && !escalating) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [sending, escalating]);

  useEffect(() => { draftRef.current = draft; }, [draft]);
  // `currentThreadKey` mirrors the render's `showList`, and that reads the ticket count.
  useEffect(() => { ticketsRef.current = tickets; }, [tickets]);
  useEffect(() => { attachmentsRef.current = attachments; }, [attachments]);

  const panelBg = useColorModeValue("#FFFFFF", "#212330");
  const bodyBg = useColorModeValue("#F9F9FB", "#191B26");
  const headerBg = useColorModeValue("brand.300", "#2C2F40");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedColor = useColorModeValue("#5A6B6C", "#A9B2C3");
  const textColor = useColorModeValue("#092728", "#F9F9FB");
  const userBubbleBg = useColorModeValue("#E2F3F3", "#2E8288");
  const userBubbleColor = useColorModeValue("#092728", "#F9F9FB");
  const agentBubbleBg = useColorModeValue("#FFFFFF", "#2C2F40");
  const agentBubbleColor = useColorModeValue("#092728", "#F9F9FB");
  const cardBg = useColorModeValue("#FFFFFF", "#2C2F40");
  const inputBg = useColorModeValue("#FFFFFF", "#2C2F40");
  const errorBg = useColorModeValue("red.50", "red.900");
  const errorColor = useColorModeValue("red.700", "red.100");
  const userLinkColor = useColorModeValue("#1A6E72", "#D6F3F4");
  const agentLinkColor = useColorModeValue("#3182ce", "#86C4FF");
  const chipBg = useColorModeValue("#F1F3F7", "#3A3E52");

  const pathname = location?.pathname || window.location.pathname;
  // SS-4245: where the composer runs edge to edge, the closed launcher sits on its Send
  // button and takes the tap meant to send the message. Only the launcher steps aside:
  // the widget stays mounted, so unread counts, realtime and openSupportChat() keep
  // working, and an open panel is not closed by navigating into a conversation.
  const [panelDropped] = useMediaQuery(PANEL_DROPPED_QUERY, { ssr: false });
  // AppUI's call, with its default fallback kept: between Chakra's ranges there are 0.02em
  // gaps where no query matches, and AppUI reads those as `base` (mobile).
  const appMobile = useBreakpointValue({ base: true, md: false }, { ssr: false, fallback: "base" });
  const launcherHidden =
    EDGE_COMPOSER_ROUTE.test(pathname) ||
    ((panelDropped || !!appMobile || !!isPatient) && PANEL_COMPOSER_ROUTE.test(pathname));

  const buildCurrentPage = useCallback(
    () => ({
      path: pathname,
      url: `${window.location.origin}${pathname}`,
      title: document.title,
    }),
    [pathname]
  );

  const fetchTicket = useCallback(async (id) => {
    const res = await cqAPI.get(`/support/tickets/${id}`);
    if (!res || res.type !== "ok" || !res.data) return null;
    return res.data;
  }, []);

  // Fetching is read-only, so the unread badge survives background refreshes.
  // Only a thread the customer is actually looking at gets marked read.
  // `upToMessageId` names the message the timestamp refers to. A reply committed
  // after this thread rendered can carry the same millisecond, so the time alone
  // cannot tell the server whether it was on screen — and treating it as read would
  // discharge the notification for an answer the customer never saw.
  const markRead = useCallback(async (id, upTo, upToMessageId) => {
    if (!id) return;
    try {
      const res = await cqAPI.post(
        `/support/tickets/${id}/read`,
        upTo ? { upTo, upToMessageId } : {},
        WRITE_TIMEOUT_MS
      );
      // Only once the server agrees. A refusal — a message it does not have, a
      // timestamp it will not take — leaves the thread unread on the server, and
      // clearing the badge locally anyway shows read while it is not, until the next
      // refresh puts it back.
      if (res && res.type === "ok") {
        setTickets((prev) => prev.map((t) => (t._id === id ? { ...t, unread: false } : t)));
      }
    } catch (e) {
      console.error("support widget: failed to mark conversation read", e);
    }
  }, []);

  const loadTickets = useCallback(async () => {
    const token = tenantToken();
    // A refresh, not a move: it re-reads whatever is on screen rather than choosing it,
    // so it records where the customer is and yields if they go elsewhere.
    const nav = navToken();
    // ...and which refresh this is. Realtime fires one per event, so two can be in
    // flight after two quick replies and they need not come back in order; the older
    // one landing last puts the list back to before the newest message.
    const at = (listIssuedRef.current += 1);
    try {
      const res = await cqAPI.get("/support/tickets");
      // The NEWEST request wins, not merely "newer than what is on screen". Those differ
      // exactly when the newest one fails: it shows its error and renders nothing, so
      // the rendered mark does not move — and an older response, issued before the reply
      // that caused all this, then passed the test, installed a list without that reply
      // in it and cleared the error. Realtime fires once per event, so nothing came back
      // for it. Numbered against what was ISSUED, a response that has been overtaken is
      // dropped whether or not the one that overtook it succeeded.
      if (isStale(token) || at !== listIssuedRef.current) return;
      if (!res || res.type !== "ok") {
        setError((res && res.msg) || GENERIC_LOAD_ERROR);
        return;
      }
      const list = Array.isArray(res.data) ? res.data : [];
      // Park BEFORE the count lands, because the count is part of the composer's identity.
      //
      // Until the first load returns, `ticketsRef` is empty and the widget shows a
      // new-conversation composer — so the customer can already be typing. This response
      // then moves the ground under them: one ticket auto-opens the thread, several
      // switch the render to the list. Either way `currentThreadKey()` stops saying
      // NEW_THREAD, and a later `startNewConversation` could no longer find their words
      // to put back. Parked here, while the key still names the composer on screen.
      if (!activeIdRef.current) parkComposer();

      // The list is the list whichever thread they are looking at, so it lands whether
      // or not they have navigated — only its freshness gates it.
      listRenderedRef.current = at;
      setTickets(list);
      setError("");

      // A single conversation opens straight away; the list only earns its place
      // once there is more than one to choose from.
      const auto = !activeIdRef.current && !customerSteeredRef.current;
      const target =
        activeIdRef.current ||
        (auto && !composeNewRef.current && list.length === 1 ? list[0]._id : null);
      if (!target) {
        // Nothing to open — so THIS is the moment the new-conversation composer is
        // settled, and the moment to offer back anything held for it.
        //
        // A first message is the case that needed it. A customer with no tickets writes
        // one, the send comes back "sign in again", and the widget goes with the session
        // before the failure can put their words anywhere. Their message is recorded
        // under NEW_THREAD and becomes offerable — but on the way back the list is empty,
        // so this function returned here, and neither Back nor "New conversation" is
        // rendered with no tickets to leave or add to. Both of the paths that restore
        // NEW_THREAD were unreachable, and the registry does not replay what it already
        // holds when a listener subscribes. Their first message sat in module memory
        // until the tab was closed.
        //
        // Read from the ARRIVING list rather than `currentThreadKey()`. That helper reads
        // `ticketsRef`, which an effect maintains and which still holds the previous
        // count at this point — so for somebody who had no tickets it answers
        // NEW_THREAD even as a list of several is about to render. Nothing is lost
        // either way (a restored message stays on the books, and the next navigation
        // replaces the composer), but the condition should be about the list that is
        // landing, not the one being replaced.
        if (list.length === 0 || composeNewRef.current) {
          restoreHeldSendRef.current?.(NEW_THREAD);
        }
        return;
      }
      // A number of its own, taken here rather than at the top: the thread read has not
      // been issued until now, and numbering it with the list read would order it against
      // requests it did not race.
      const threadAt = (threadIssuedRef.current += 1);
      newestThreadReadRef.current = threadAt;
      const full = await fetchTicket(target);
      if (isStale(token) || navMoved(nav)) return;
      if (!full) {
        // SS-4213: taken off a shared thread. The server no longer lists it for them AND
        // will not hand it over, so leave it — otherwise they are stranded on a stale copy,
        // with a composer aimed at a conversation they can no longer reach and, if it was
        // their only one, no Back button either.
        // Only on the NEWEST reads: an older refusal must not evict a thread a later refresh
        // has already shown again (removed, then put back).
        if (
          target === activeIdRef.current
          && threadAt === newestThreadReadRef.current
          && at === listRenderedRef.current
          && !list.some((t) => String(t._id) === String(target))
        ) {
          // Their draft for that thread stays with it (parked), and the composer they are
          // shown now is the new-conversation one — a reply meant for the old thread must
          // not quietly become a brand-new conversation.
          parkComposer();
          activeIdRef.current = null;
          setActiveTicket(null);
          adoptComposer(NEW_THREAD);
        }
        return;
      }
      // Nothing was open when this started, so the widget is choosing the only
      // conversation there is. Its composer is adopted like any other opening — the
      // new-conversation draft the customer may have been typing was parked above, and
      // without adopting, that draft simply stays in the box and Send posts it to this
      // old ticket instead.
      //
      // Deliberately no `markRead`, though: this runs on mount whether or not the panel
      // is open, and marking a thread read that nobody has looked at clears the badge for
      // a reply the customer never saw. `openTicket` does that when they actually open
      // one.
      if (auto) {
        // Only if this response's LIST is still the one on screen. `navMoved` clears the
        // customer of navigating, but not the widget of learning more: a newer refresh
        // can have rendered a two-ticket list while this one's detail read was still in
        // flight, and opening a thread over that list yanks somebody out of a screen
        // they were quietly reading — off an older answer to a question nobody re-asked.
        if (at !== listRenderedRef.current) return;
        // ...and park what they typed while the detail read ran. The park above happened
        // before this await; anything written since is in the live composer and belongs
        // to the new conversation they thought they were writing, not to this old
        // thread. The key still says NEW_THREAD — `activeIdRef` is not set yet — so it
        // lands where `startNewConversation` will look for it.
        parkComposer();
        activeIdRef.current = full._id;
        adoptComposer(full._id);
        // ...and a message this customer sent that never reached the server — held from
        // a session that ended under it, or from a failure while they were elsewhere —
        // is put back now. `openTicket` does this for a thread they choose; a customer
        // with a single conversation never chooses one, so without this their words
        // would sit in the registry unseen.
        restoreHeldSend(full._id);
        applyThread(full, threadAt, { fromRead: true });
        return;
      }
      // Refreshing the thread they are already on. `navMoved` has cleared them of
      // navigating; this catches the rest — a send that created a thread and adopted
      // it, say — because painting A's contents under B's composer is the whole bug.
      //
      // `applyThread` then answers the question neither guard can: two refreshes of THIS
      // thread, both legitimate, landing in the wrong order. Passing the list check does
      // not order the second read — an older snapshot fetched first can still return
      // last and take the newest reply back off the screen.
      if (activeIdRef.current !== full._id) return;
      applyThread(full, threadAt, { fromRead: true });
    } catch (e) {
      if (isStale(token)) return;
      console.error("support widget: failed to load tickets", e);
      setError(GENERIC_LOAD_ERROR);
    }
  }, [fetchTicket, applyThread, parkComposer, adoptComposer, restoreHeldSend]);

  const openTicket = useCallback(
    async (id) => {
      customerSteeredRef.current = true;
      composeNewRef.current = false;
      setComposeNew(false);
      setError("");
      const token = tenantToken();
      // This IS the move, so it claims the view: a slower `openTicket(A)` that has not
      // come back yet, and any refresh in flight, are now stale and drop their results.
      const nav = ++navRef.current;
      // The spinner belongs to the SET of opens in flight, not to whichever one started
      // it. Clearing it from the winner's `finally` alone means a superseded open leaves
      // it up if nothing else takes responsibility, and reasoning case by case about who
      // that is invites exactly the stuck spinner it is meant to avoid.
      openingRef.current += 1;
      setLoading(true);
      // A deliberate open is the newest word on this thread by definition, so it takes
      // its number here — ahead of any refresh already running.
      const threadAt = (threadIssuedRef.current += 1);
      newestThreadReadRef.current = threadAt;
      try {
        const full = await fetchTicket(id);
        if (isStale(token) || navMoved(nav)) return;
        if (!full) {
          setError(GENERIC_LOAD_ERROR);
          return;
        }
        // Park whatever is in the box for the thread being left, then pick up this
        // one's. Without this the composer follows the customer between conversations
        // and Send addresses whichever one they happen to be looking at.
        parkComposer();
        activeIdRef.current = full._id;
        applyThread(full, threadAt, { fromRead: true });
        adoptComposer(full._id);
        markRead(full._id, lastMessageAt(full), lastShownMessage(full)?._id);

        restoreHeldSend(full._id);
      } catch (e) {
        if (isStale(token) || navMoved(nav)) return;
        console.error("support widget: failed to open ticket", e);
        setError(GENERIC_LOAD_ERROR);
      } finally {
        openingRef.current -= 1;
        if (openingRef.current <= 0 && !isStale(token)) setLoading(false);
      }
    },
    [fetchTicket, markRead, parkComposer, adoptComposer, restoreHeldSend, applyThread]
  );

  const backToList = useCallback(() => {
    // Park it before leaving, so it is still here when they come back — and, more to
    // the point, so it is not still in the box when they open a different thread.
    parkComposer();
    customerSteeredRef.current = true;
    navRef.current += 1;
    composeNewRef.current = false;
    setComposeNew(false);
    activeIdRef.current = null;
    setActiveTicket(null);
    setError("");
    // Where Back LANDS depends on how many conversations there are, and the composer has
    // to match the landing. With any conversation at all, it is the list — one row still
    // carries the "New conversation" button, which is what makes a parked new-thread
    // draft and a held failed send reachable in the one-ticket world at all. With none,
    // Back lands straight on the new-conversation composer — whose key is NEW_THREAD —
    // so it adopts what is parked there instead of blanking it.
    if (ticketsRef.current.length >= 1) {
      // Eagerly, like adoptComposer: the refs answer "is the composer free?" and must
      // never lag a commit behind what was just decided.
      draftRef.current = "";
      attachmentsRef.current = [];
      setDraft("");
      setAttachments([]);
    } else {
      adoptComposer(NEW_THREAD);
      restoreHeldSend(NEW_THREAD);
    }
    // Whatever was being fetched is for a thread they have left, and its own `finally`
    // now yields to this move rather than clearing the spinner over the list.
    setLoading(false);
  }, [parkComposer, adoptComposer, restoreHeldSend]);

  const startNewConversation = useCallback(() => {
    parkComposer();
    customerSteeredRef.current = true;
    navRef.current += 1;
    composeNewRef.current = true;
    setComposeNew(true);
    activeIdRef.current = null;
    setActiveTicket(null);
    adoptComposer(NEW_THREAD);
    setError("");
    setLoading(false);
    // A send that failed while this was still an unsent conversation is held under
    // NEW_THREAD, and this is the only place it can ever come back: `openTicket`
    // restores by real ticket id, and a conversation that was never created has none.
    // Without this the body, files and retry id sit unreachable until a reload throws
    // them away.
    restoreHeldSend(NEW_THREAD);
  }, [parkComposer, adoptComposer, restoreHeldSend]);

  const removeAttachment = useCallback((id) => {
    setAttachments((current) => current.filter((a) => a._id !== id));
    setAttachError("");
  }, []);

  /**
   * Apply an upload's outcome to the composer that STARTED it, wherever it now lives.
   *
   * An upload outlives the screen that began it: pick a file on A, press Back, open B,
   * and the request is still running. `setAttachments` only ever touches the composer on
   * screen, so the finished URL landed on B — where no such entry exists, so nothing
   * happened at all — while A's entry sat parked as `{uploading: true}` for good. Reopen
   * A and the file is still spinning, and Send refuses to go while it is.
   */
  const resolveAttachment = useCallback((threadKey, change) => {
    if (!threadKey || currentThreadKey() === threadKey) {
      setAttachments(change);
      return;
    }
    const parked = composersRef.current[threadKey];
    // Nothing parked means the composer was emptied while this ran — the message was
    // sent, or the thread was left with nothing in it. There is no entry to resolve.
    if (!parked) return;
    const attachments_ = change(parked.attachments || []);
    if (attachments_.length || parked.draft) {
      composersRef.current[threadKey] = { ...parked, attachments: attachments_ };
    } else {
      delete composersRef.current[threadKey];
    }
  }, []);

  // Files are uploaded as they are picked, so the send itself only carries the
  // URLs the endpoint gave back. A file the server would refuse is turned away
  // here, and the ones that are fine still go up.
  const attachFiles = useCallback(
    async (files) => {
      const picked = Array.from(files || []);
      if (!picked.length) return;

      const token = tenantToken();
      // The composer these files belong to, fixed now. Everything below resolves into
      // this one, not into whichever composer is on screen when the upload finishes.
      const startedOn = currentThreadKey();
      let room = MAX_ATTACHMENTS - attachments.length;
      let rejection = "";
      const queued = [];

      picked.forEach((file) => {
        const problem = attachmentRejection(file, room);
        if (problem) {
          rejection = rejection || problem;
          return;
        }
        room -= 1;
        queued.push({
          file,
          entry: {
            _id: newClientMessageId(),
            name: file.name || "Attachment",
            size: file.size || 0,
            content_type: String(file.type || "").toLowerCase(),
            uploading: true,
          },
        });
      });

      setAttachError(rejection);
      if (!queued.length) return;
      setAttachments((current) => [...current, ...queued.map((q) => q.entry)]);

      for (const { file, entry } of queued) {
        try {
          const res = await uploadAttachment(file, bid);
          if (isStale(token)) return;
          if (!res || res.type !== "ok" || !res.url) {
            resolveAttachment(startedOn, (current) => current.filter((a) => a._id !== entry._id));
            // The error line belongs to the screen, not to the parked composer, so it is
            // only worth showing while they are still looking at the thread it is about.
            if (currentThreadKey() === startedOn) setAttachError((res && res.msg) || GENERIC_UPLOAD_ERROR);
            continue;
          }
          resolveAttachment(startedOn, (current) =>
            current.map((a) => (a._id === entry._id ? { ...a, uploading: false, url: res.url } : a))
          );
        } catch (e) {
          if (isStale(token)) return;
          console.error("support widget: failed to upload an attachment", e);
          resolveAttachment(startedOn, (current) => current.filter((a) => a._id !== entry._id));
          if (currentThreadKey() === startedOn) setAttachError(GENERIC_UPLOAD_ERROR);
        }
      }
    },
    [attachments.length, resolveAttachment, bid]
  );

  // A private file cannot be linked to directly — it needs the bearer token — so
  // it is fetched and handed to the browser. An image is already in the object-URL
  // map from rendering its thumbnail, and opening that costs nothing; anything
  // else is downloaded, because a tab opened after the fetch would be swallowed
  // by the popup blocker.
  const openAttachment = useCallback(async (attachment, message) => {
    if (!attachment || attachment.ref == null) return;
    const type = safeContentType(attachment);
    const cached = attachmentBlobsRef.current[attachmentKeyFor(message, attachment)];
    // A tab is only ever opened on a file whose recorded type we trust.
    if (type && cached && cached !== BLOB_LOADING) {
      window.open(cached, "_blank", "noopener,noreferrer");
      return;
    }
    const token = tenantToken();
    try {
      const blobUrl = await attachmentBlobUrl(attachmentEndpoint(activeIdRef.current, attachment.ref, message?._id), type || OPAQUE_TYPE);
      if (isStale(token)) {
        if (blobUrl) window.URL.revokeObjectURL(blobUrl);
        return;
      }
      if (!blobUrl) {
        setError("We couldn't open that file. Please try again.");
        return;
      }
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", attachment.name || "attachment");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      if (isStale(token)) return;
      console.error("support widget: failed to open an attachment", e);
      setError("We couldn't open that file. Please try again.");
    }
  }, []);

  const sendMessage = useCallback(async () => {
    const body = draft.trim();
    // A message may be nothing but attachments, but never a half-uploaded one.
    // The send button is already disabled while one is in flight; this catches
    // the Enter key, which does not go through it.
    const ready = attachments.filter((a) => a.url && !a.uploading);
    // Not while an escalation is in flight either, and for the same reason it is not
    // offered while a send is. Both return the WHOLE ticket and `applyWrite` orders them
    // by arrival, so a follow-up that commits first is erased by the escalation's older
    // payload landing after it — and the customer's message disappears from a thread it
    // is genuinely on. The held-send record has been retired by then, so resending mints
    // a fresh client id and stores it twice. The guard was only ever one-directional.
    if (sending || escalating) return;
    if (attachments.length !== ready.length) {
      setAttachError("Wait for the files to finish uploading.");
      return;
    }
    if (!body && !ready.length) return;

    const ticketId = activeIdRef.current;
    // Reuse the id of a failed send: retrying with a fresh one would let the
    // server treat an attempt it already stored as a second message. Scoped to the
    // conversation, so starting a new one with the same text is a new send and not
    // a "duplicate" that silently reopens the old thread.
    const scope = ticketId || "new";
    // The files are part of what was sent, so a retry only counts as the same
    // attempt when they are the same ones.
    const attachmentKey = ready.map((a) => a.url).join("|");
    const clientMessageId =
      retryRef.current &&
      retryRef.current.scope === scope &&
      retryRef.current.body === body &&
      retryRef.current.attachmentKey === attachmentKey
        ? retryRef.current.clientMessageId
        : newClientMessageId();
    retryRef.current = { scope, body, attachmentKey, clientMessageId };
    const token = tenantToken();
    // The thread this send belongs to, fixed at the moment it starts. Everything below
    // that touches the composer or the thread view is conditional on the customer
    // still being here when it lands.
    const sentOn = ticketId || NEW_THREAD;
    // ...and the navigation generation, but ONLY for an unsent conversation.
    //
    // A ticket id identifies itself: come back to A after wandering off and the send that
    // was running is still A's, so its answer belongs on screen. Requiring the generation
    // there would throw that answer away and leave the customer's own message missing
    // until something else refetched.
    //
    // `NEW_THREAD` is not an identity. It names "whatever new conversation is being
    // written", and there can be a second one: send an unsent conversation, press Back,
    // start another. The key alone said the customer was still here — so the first send's
    // answer opened ITS ticket under the second one's draft, and the next Send posted
    // those words to it. A failure did the mirror image, restoring the first body and
    // files into the second composer. The generation is what tells the two apart.
    const nav = navToken();
    const stillHere = () => !isStale(token)
      && currentThreadKey() === sentOn
      && (sentOn !== NEW_THREAD || !navMoved(nav));
    setPendingFor(sentOn);
    setDraft("");
    setAttachments([]);
    // The mirrors eagerly, as `adoptComposer` does. The failure path asks them whether
    // the composer is free, and a rejection can land before React commits these clears —
    // so it would read the words it is about to hand back as somebody else's draft, and
    // hold the message instead of restoring it.
    draftRef.current = "";
    attachmentsRef.current = [];
    setAttachError("");
    setError("");
    setPendingMessages([
      {
        _id: clientMessageId,
        role: "user",
        body,
        attachments: ready.map(attachmentPayload),
        created: new Date().toISOString(),
        pending: true,
      },
    ]);
    setSending(true);
    // Whatever the composer was holding is superseded by this send — the same message
    // going out again, or the rewrite sent in its place.
    if (composerHeldRef.current) {
      dropSend(composerHeldRef.current);
      composerHeldRef.current = null;
    }
    // Recorded BEFORE the await. The composer has just been cleared, so this is the only
    // copy, and the widget can be taken off the screen before any branch below runs.
    const heldEntry = recordSend(heldKey(sentOn), { body, attachments: ready, scope, attachmentKey, clientMessageId });
    try {
      const res = await cqAPI.post(
        ticketId ? `/support/tickets/${ticketId}/messages` : "/support/tickets",
        {
          message: body,
          clientMessageId,
          currentPage: buildCurrentPage(),
          attachments: ready.map(attachmentPayload),
          // The last reply this screen had actually rendered when they wrote. Sending
          // is not, on its own, evidence of having read the whole thread: an agent can
          // reply while the customer is typing, and that reply was never on screen.
          // Without this the server discharges its obligation to tell them about it,
          // and they are left with an answer nobody ever announces.
          lastRenderedMessageId: lastShownMessage(activeTicket)?._id,
        },
        WRITE_TIMEOUT_MS
      );
      // The server's verdict, read BEFORE anything can return early. A send that
      // COMMITTED while the customer was logging out used to skip its discharge here and
      // be marked failed below — so the message they had actually sent came back into
      // their composer, over an error, on their next visit.
      if (res && res.type === "ok" && res.data) dropSend(heldEntry);
      if (isStale(token)) return;
      if (!res || res.type !== "ok" || !res.data) {
        // They moved on. Their words are not lost — they are held for the thread they
        // were written on, and put back when it is reopened.
        // They moved on. Their words stay recorded for the thread they were written on,
        // and are put back when it is reopened.
        if (!stillHere()) return;
        setError((res && res.msg) || GENERIC_SEND_ERROR);
        // The uploaded files come back with the words rather than the customer having
        // to pick them again: a retry re-sends the same URLs instead of re-uploading.
        restoreOrHold(heldEntry, body, ready);
        return;
      }
      const ticket = res.data;
      // Sent, so there is nothing parked for this thread any more — but only if this send
      // still owns the composer. Deleting unconditionally destroyed work: send on A, come
      // back to A while it is still running, type something new, go to the list, and that
      // NEWER draft is what is parked under A when the original send finally succeeds.
      // Same for `NEW_THREAD`, where the customer may have started another one since.
      // If they have moved on, whatever is parked belongs to a later act than this send.
      if (stillHere()) {
        delete composersRef.current[sentOn];
        delete composersRef.current[ticket._id];
      }
      // This send is STORED, so its idempotency token is spent — and it must be spent
      // everywhere it lives, not just in the live composer. `parkComposer` snapshots
      // `retryRef` while a send is still running, so the token can be sitting inside a
      // parked entry: type the same words again while the send runs, park them, and the
      // adopted token would make the next send "a retry" of a message that already
      // committed — the server returns success and stores nothing, and somebody's
      // deliberate second message is silently swallowed.
      //
      // Scrubbed BY ID, never wholesale: the live `retryRef` may by now hold another
      // thread's token (adoptComposer replaces it), and that one still protects a send
      // whose response may have been lost. Only this send's own token is spent.
      const spent = (record) =>
        (record && record.clientMessageId === clientMessageId ? null : record);
      retryRef.current = spent(retryRef.current);
      for (const key of Object.keys(composersRef.current)) {
        const parked = composersRef.current[key];
        if (parked.retry !== spent(parked.retry)) {
          composersRef.current[key] = { ...parked, retry: null };
        }
      }
      // The list always learns about it. Forcing the VIEW onto this thread only makes
      // sense if the customer is still on it — otherwise a slow send drags them out of
      // whatever they opened while they waited.
      if (stillHere()) {
        // A first send turns the unsent conversation into a real one, so this changes
        // which thread is on screen: it takes the view the same way a tap does, and a
        // refresh still fetching the old key drops its result rather than painting it.
        navRef.current += 1;
        composeNewRef.current = false;
        setComposeNew(false);
        activeIdRef.current = ticket._id;
        // Numbered HERE, not when the send started: this thread now contains the message
        // that was just stored, so it is newer than anything a refresh issued earlier can
        // be holding, however long the send took.
        applyWrite(ticket);
      }
      // The list row for this thread is now newer than any list read already in flight:
      // one issued before the POST read a list without this message in it, and without
      // taking a number here it would still pass the freshness check and put the old
      // preview and status back. A customer message on a thread a human already owns
      // raises no realtime refresh of its own, so pressing Back would show that stale row
      // until some unrelated event came along.
      listRenderedRef.current = (listIssuedRef.current += 1);
      setTickets((prev) => [
        summarize(ticket),
        ...prev.filter((t) => t._id !== ticket._id),
      ]);
      // The composer just emptied for good, so anything held for this conversation —
      // an earlier failure that had to wait because the customer was mid-draft — gets
      // its turn now rather than waiting for them to navigate away and back.
      if (stillHere()) restoreHeldSend(currentThreadKey());
    } catch (e) {
      if (isStale(token)) return;
      console.error("support widget: failed to send message", e);
      if (!stillHere()) return;
      setError(GENERIC_SEND_ERROR);
      restoreOrHold(heldEntry, body, ready);
    } finally {
      // The request has finished, whatever became of it. Marked HERE and nowhere else:
      // every branch above can return early — the tenant changed, the customer moved on
      // — and an `inFlight` left standing in any of those paths would strand the message
      // in the registry forever, never offered back to anyone. Success has already
      // removed the record, so anything still here failed; and if the customer is no
      // longer on the thread it was written for, whatever screen they ARE on is told to
      // look, since nothing else would notice.
      settleSend(heldEntry, { orphaned: !stillHere() });
      if (!isStale(token)) {
        setPendingMessages([]);
        setPendingFor(null);
        setSending(false);
      }
    }
    // `activeTicket` and `bid` are read inside, so they belong here: without them the
    // callback keeps whichever ticket it closed over, and `lastRenderedMessageId` then
    // names a message from before the last realtime refresh. The server treats a
    // position behind the recorded one as no position at all, so the effect is a
    // missed discharge rather than a wrong one — but a stale closure is not something
    // to leave in place and rely on the server to absorb.
    // `escalating` is read by the guard above, so it belongs here — without it this
    // callback closes over the value from the render before the escalation started and
    // the guard never sees it.
  }, [draft, attachments, sending, escalating, buildCurrentPage, activeTicket, bid, applyWrite]);

  // The assistant's answers carry validated in-app paths. Navigate client-side and
  // keep the panel open, so the customer can read the instructions on the page they
  // were just sent to.
  const goTo = useCallback(
    (path) => {
      if (!path || !path.startsWith("/")) return;
      const currentBid = bid || getBid();
      navigate(currentBid ? `${path}?bid=${currentBid}` : path);
    },
    [navigate, bid]
  );

  const escalate = useCallback(async () => {
    const ticketId = activeIdRef.current;
    if (!ticketId || escalating) return;
    const token = tenantToken();
    // "Talk to a human" waits on the server like any other request, and the customer can
    // press Back and open another conversation while it does. Its answer is a whole
    // ticket, so applying it unconditionally paints THIS thread over whatever they moved
    // to — and, because only `openTicket` adopts a composer, under the other thread's
    // draft. The next Send then posts those words here; if they only went back to the
    // list, `activeIdRef` is null and Send opens a new conversation with them instead.
    const nav = navToken();
    setEscalating(true);
    setError("");
    try {
      const res = await cqAPI.post(`/support/tickets/${ticketId}/escalate`, {}, WRITE_TIMEOUT_MS, SEND_ONCE);
      if (isStale(token) || navMoved(nav) || activeIdRef.current !== ticketId) return;
      if (!res || res.type !== "ok" || !res.data) {
        setError((res && res.msg) || "We couldn't reach the team. Please try again.");
        return;
      }
      applyWrite(res.data);
    } catch (e) {
      if (isStale(token) || navMoved(nav)) return;
      console.error("support widget: failed to escalate ticket", e);
      setError("We couldn't reach the team. Please try again.");
    } finally {
      // ALWAYS cleared, unlike the guarded branches above. `escalating` is one flag for
      // the whole widget, not per thread: leaving it set because the customer navigated
      // meant the next AI conversation they opened showed a "Talk to a human" button
      // stuck loading and refusing to fire, with nothing to clear it — closing and
      // reopening the panel does not remount the widget. The RESULT is what navigation
      // invalidates; the fact that the request finished is true wherever they are.
      if (!isStale(token)) setEscalating(false);
    }
  }, [escalating, applyWrite]);

  // Keyed on bid AND uid: switching practice has to drop the previous tenant's
  // conversation, draft and retry state, not just re-fetch around them.
  useEffect(() => {
    tenantRef.current = `${uid || ""}:${bid || ""}`;
    customerSteeredRef.current = false;
    navRef.current += 1;
    // The sequence counters are deliberately NOT reset. They only ever go up, so a fresh
    // read always outranks anything already rendered, and zeroing them while a request is
    // in flight would let that request back in — this effect re-runs when `visible`
    // toggles, where the tenant token is unchanged and would not catch it.
    activeIdRef.current = null;
    composeNewRef.current = false;
    retryRef.current = null;
    // Another PERSON's records go; this customer's stay, whichever practice they belong
    // to. Deliberately not scoped to the practice: switching practices is the same
    // person, their records are keyed by practice already so nothing can cross over, and
    // deleting them would throw away a message still in flight for the one they left —
    // its own `finally` could then find nothing to mark. The effect also runs on mount,
    // which is why clearing wholesale would destroy the very message an expired session
    // was meant to preserve.
    forgetOtherUsers(user?.id);
    composersRef.current = {};
    setTickets([]);
    setActiveTicket(null);
    setComposeNew(false);
    setPendingMessages([]);
    setDraft("");
    setAttachments([]);
    setAttachError("");
    setError("");
    // The previous tenant's files must not stay readable in this tab either.
    Object.values(attachmentBlobsRef.current).forEach((url) => {
      if (url && url !== BLOB_LOADING) window.URL.revokeObjectURL(url);
    });
    attachmentBlobsRef.current = {};
    setAttachmentBlobs({});

    if (!visible || !bid || !uid) {
      setIsOpen(false);
      return;
    }
    setLoading(true);
    loadTickets().finally(() => setLoading(false));
  }, [visible, bid, uid, loadTickets]);

  // A tenant change REMOUNTS the widget (see SupportWidgetMount), so the reset
  // above never runs for the instance being torn down and its object URLs would
  // pin the file bytes until the tab is closed. Dropping the tenant token is what
  // stops a download that is still in flight from storing one more: it makes the
  // token it captured stale, so it revokes what it fetched instead.
  useEffect(
    () => () => {
      tenantRef.current = null;
      Object.values(attachmentBlobsRef.current).forEach((url) => {
        if (url && url !== BLOB_LOADING) window.URL.revokeObjectURL(url);
      });
      attachmentBlobsRef.current = {};
    },
    []
  );

  useEffect(() => {
    if (!visible || !bid || !uid || !realtimeInitialized) return undefined;
    const topic = `_support_ticket_update/${bid}/${uid}`;
    const handler = () => loadTickets();
    let client = null;
    // AppContext rebuilds realtime.current in a parent effect, which React runs after
    // this one, so grab the client once the current commit's effects have all flushed.
    const timer = setTimeout(() => {
      client = realtime?.current;
      if (client) client.subscribeToTopic(topic, bid, handler);
    }, 0);
    return () => {
      clearTimeout(timer);
      if (client) client.unsubscribeFromTopic(topic, bid, handler);
    };
  }, [visible, bid, uid, realtime, realtimeInitialized, loadTickets]);

  // A support-reply notification links to /?support=<ticketId>. Follow it: open
  // the panel on that conversation, then drop the parameter so a later reload or
  // a shared URL does not reopen it.
  useEffect(() => {
    if (!visible) return;
    const params = new URLSearchParams(window.location.search);
    const ticketId = params.get("support");
    if (!ticketId) return;

    setIsOpen(true);
    openTicket(ticketId);

    params.delete("support");
    const query = params.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}`
    );
  }, [visible, openTicket]);

  useEffect(() => {
    if (!visible) return undefined;
    const onOpenRequest = (event) => {
      setIsOpen(true);
      const question = event?.detail?.question;
      if (!question) return;

      // A question handed over from the PO chat is a NEW question, and it opens its own
      // conversation. `startNewConversation` restores whatever was parked under
      // NEW_THREAD — a half-written new request the customer had going, INCLUDING its
      // staged files and retry token. Overwriting only the draft left that screenshot
      // attached to the PO question and let a stale retry token turn it into a
      // "duplicate". The handoff is a fresh request, so the whole composer is cleared to
      // exactly the question.
      startNewConversation();
      const handoff = brandSafe(String(question).slice(0, MAX_MESSAGE_CHARS), brand);
      draftRef.current = handoff;
      attachmentsRef.current = [];
      retryRef.current = null;
      setDraft(handoff);
      setAttachments([]);
      setAttachError("");
    };
    window.addEventListener(OPEN_SUPPORT_CHAT_EVENT, onOpenRequest);
    return () => window.removeEventListener(OPEN_SUPPORT_CHAT_EVENT, onOpenRequest);
  }, [visible, startNewConversation, brand]);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!isOpen || !viewport) return undefined;
    // The on-screen keyboard shrinks the visual viewport but not the layout viewport,
    // which would otherwise leave the composer underneath it.
    const update = () => {
      const inset = window.innerHeight - viewport.height - viewport.offsetTop;
      setKeyboardInset(inset > 120 ? Math.round(inset) : 0);
    };
    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      setKeyboardInset(0);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [isOpen, activeTicket, pendingMessages, sending]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  // Attached images are private files behind the authenticated /file endpoint, so
  // each one is fetched once and kept as an object URL for its thumbnail. Only
  // images are pre-loaded; anything else is fetched when it is actually opened.
  useEffect(() => {
    const token = tenantToken();
    // url -> the type its blob is built with, so the same file queued twice is
    // only fetched once.
    const pending = new Map();
    [...(activeTicket?.messages || []), ...(pendingFor === (activeTicket?._id || NEW_THREAD) ? pendingMessages : [])]
      .flatMap((message) => (message.attachments || []).map(a => ({ a, message })))
      .filter(({ a }) => a && a.ref != null && isImageAttachment(a))
      .forEach(({ a, message }) => {
        const key = attachmentKeyFor(message, a);
        if (attachmentBlobsRef.current[key] || pending.has(key)) return;
        pending.set(key, { ref: a.ref, type: safeContentType(a), messageId: message?._id });
      });

    pending.forEach(({ ref, type, messageId }, key) => {
      attachmentBlobsRef.current[key] = BLOB_LOADING;
      attachmentBlobUrl(attachmentEndpoint(activeTicket?._id, ref, messageId), type)
        .then((blobUrl) => {
          if (isStale(token) || !blobUrl) {
            if (blobUrl) window.URL.revokeObjectURL(blobUrl);
            delete attachmentBlobsRef.current[key];
            return;
          }
          attachmentBlobsRef.current[key] = blobUrl;
          setAttachmentBlobs((current) => ({ ...current, [key]: blobUrl }));
        })
        .catch((e) => {
          delete attachmentBlobsRef.current[key];
          console.error("support widget: failed to load an attachment", e);
        });
    });
  }, [activeTicket, pendingMessages]);

  // Opening the panel on an already-loaded thread is the other way a customer
  // actually sees it, so that counts as reading it too. Keyed on the last message
  // as well as the id, so a reply that arrives over realtime while the panel is
  // open is also marked read.
  const visibleThrough = lastMessageAt(activeTicket);
  const visibleThroughId = lastShownMessage(activeTicket)?._id;
  useEffect(() => {
    if (isOpen && activeTicket?._id) markRead(activeTicket._id, visibleThrough, visibleThroughId);
  }, [isOpen, activeTicket?._id, visibleThrough, visibleThroughId, markRead]);

  if (!visible) return null;

  const unreadCount = tickets.filter((t) => t.unread).length;

  if (!isOpen) {
    if (launcherHidden) return null;
    return (
      <Box
        position="fixed"
        zIndex="overlay"
        textAlign="left"
        right={{ base: "16px", sm: "24px" }}
        bottom={{ base: "calc(16px + env(safe-area-inset-bottom))", sm: "24px" }}
        data-testid="support-widget-launcher"
      >
        <IconButton
          aria-label={`Open ${brand} support chat`}
          icon={<ChatIcon boxSize={5} />}
          onClick={() => setIsOpen(true)}
          w="56px"
          h="56px"
          minW="56px"
          px={0}
          borderRadius="full"
          bg={headerBg}
          color="white"
          boxShadow="0 8px 24px rgba(0, 0, 0, 0.22)"
          _hover={{ bg: headerBg, opacity: 0.9 }}
          _active={{ bg: headerBg, opacity: 0.8 }}
        />
        {unreadCount > 0 && (
          <Badge
            position="absolute"
            top="-2px"
            right="-2px"
            minW="22px"
            h="22px"
            px="6px"
            borderRadius="full"
            bg="red.500"
            color="white"
            fontSize="11px"
            lineHeight="22px"
            textAlign="center"
            pointerEvents="none"
          >
            {unreadCount}
          </Badge>
        )}
      </Box>
    );
  }

  const showList = !activeTicket && !composeNew && tickets.length >= 1;
  const pendingHere = pendingFor === (activeTicket?._id || NEW_THREAD) ? pendingMessages : [];
  const messages = [...(activeTicket?.messages || []), ...pendingHere];
  const showTyping =
    sending && (!activeTicket || activeTicket.status === "ai");
  const showEscalate = !!activeTicket && activeTicket.status === "ai";
  const showBack = tickets.length >= 1 && (!!activeTicket || composeNew);
  const headerTitle = showList ? "Your conversations" : `${brand} support`;
  const headerSubtitle = activeTicket
    ? (activeTicket.status === "ai" ? `${brand} assistant` : STATUS_LABELS[activeTicket.status] || "")
    : "We usually reply in a few minutes";
  // SS-4213: a thread can have several people from the practice on it. The API names the
  // OTHERS (names only) and marks each customer message `mine` — both only on such a
  // thread, so a thread of one reads exactly as it always did.
  const colleagues = (activeTicket?.participants || []).map((p) => p && p.name).filter(Boolean);
  const sharedThread = colleagues.length > 0;
  const uploading = attachments.some((a) => a.uploading);
  // Attachments alone are a message; a half-uploaded one is not.
  const canSend = !uploading && !escalating && (!!draft.trim() || attachments.some((a) => a.url));

  const renderAttachments = (message, isUser) => {
    const list = message.attachments || [];
    if (!list.length) return null;
    return (
      <Flex
        direction="column"
        align={isUser ? "flex-end" : "flex-start"}
        gap={1}
        mt={1}
        maxW="85%"
        opacity={message.pending ? 0.6 : 1}
      >
        {list.map((attachment, index) => {
          const key = `${message._id}-${attachment.ref ?? index}`;
          if (isImageAttachment(attachment)) {
            const blobUrl = attachmentBlobs[attachmentKeyFor(message, attachment)];
            return (
              <Box
                as="button"
                type="button"
                key={key}
                onClick={() => openAttachment(attachment, message)}
                title={attachment.name}
                maxW="220px"
                lineHeight={0}
                overflow="hidden"
                borderWidth="1px"
                borderStyle="solid"
                borderColor={borderColor}
                borderRadius="12px"
              >
                {blobUrl ? (
                  <Image
                    src={blobUrl}
                    alt={attachment.name}
                    maxW="220px"
                    maxH="220px"
                    objectFit="contain"
                  />
                ) : (
                  <Flex align="center" justify="center" w="120px" h="80px">
                    <Spinner size="sm" color={mutedColor} />
                  </Flex>
                )}
              </Box>
            );
          }
          return (
            <Flex
              as="button"
              type="button"
              key={key}
              onClick={() => openAttachment(attachment, message)}
              align="center"
              wrap="wrap"
              gap={2}
              maxW="100%"
              textAlign="left"
              bg={chipBg}
              borderWidth="1px"
              borderStyle="solid"
              borderColor={borderColor}
              borderRadius="10px"
              px={2}
              py={1}
            >
              <AttachmentIcon boxSize={3} color={mutedColor} />
              <Text fontSize="xs" wordBreak="break-word">
                {attachment.name}
              </Text>
              <Text fontSize="10px" color={mutedColor}>
                {formatBytes(attachment.size)}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    );
  };

  return (
    <Flex
      direction="column"
      position="fixed"
      zIndex="overlay"
      textAlign="left"
      overflow="hidden"
      bg={panelBg}
      color={textColor}
      borderWidth={{ base: 0, sm: "1px" }}
      borderStyle="solid"
      borderColor={borderColor}
      borderRadius={{ base: 0, sm: "16px" }}
      boxShadow={{ base: "none", sm: "0 16px 48px rgba(0, 0, 0, 0.24)" }}
      top={{ base: 0, sm: "auto" }}
      left={{ base: 0, sm: "auto" }}
      right={{ base: 0, sm: "24px" }}
      bottom={{ base: keyboardInset ? `${keyboardInset}px` : 0, sm: "24px" }}
      width={{ base: "auto", sm: "380px" }}
      maxWidth="100vw"
      height={{ base: "auto", sm: "600px" }}
      maxHeight={{ base: "none", sm: "calc(100vh - 48px)" }}
      data-testid="support-widget-panel"
    >
      <Flex
        align="center"
        gap={2}
        flexShrink={0}
        bg={headerBg}
        color="white"
        px={4}
        pt={{ base: "calc(12px + env(safe-area-inset-top))", sm: "12px" }}
        pb="12px"
      >
        {showBack && (
          <IconButton
            aria-label="Back to your conversations"
            icon={<ArrowBackIcon />}
            onClick={backToList}
            variant="ghost"
            minW="32px"
            h="32px"
            px={0}
            borderRadius="full"
            color="white"
            _hover={{ bg: "whiteAlpha.300" }}
            _active={{ bg: "whiteAlpha.400" }}
          />
        )}
        <Box flex="1" minW={0}>
          <Text fontWeight="bold" fontSize="md" noOfLines={1}>
            {headerTitle}
          </Text>
          {!!headerSubtitle && (
            <Text fontSize="xs" opacity={0.85} noOfLines={1}>
              {headerSubtitle}
            </Text>
          )}
        </Box>
        <IconButton
          aria-label="Close support chat"
          icon={<CloseIcon boxSize={3} />}
          onClick={() => setIsOpen(false)}
          variant="ghost"
          minW="32px"
          h="32px"
          px={0}
          borderRadius="full"
          color="white"
          _hover={{ bg: "whiteAlpha.300" }}
          _active={{ bg: "whiteAlpha.400" }}
        />
      </Flex>

      {!!error && (
        <Flex
          align="center"
          justify="space-between"
          gap={2}
          flexShrink={0}
          bg={errorBg}
          color={errorColor}
          px={4}
          py={2}
        >
          <Text fontSize="xs">{error}</Text>
          <CloseButton size="sm" onClick={() => setError("")} />
        </Flex>
      )}

      <Box ref={scrollRef} flex="1" minH={0} overflowY="auto" bg={bodyBg} px={4} py={3}>
        {loading && !messages.length ? (
          <Flex justify="center" py={8}>
            <Spinner size="md" color={mutedColor} />
          </Flex>
        ) : showList ? (
          <VStack align="stretch" spacing={2}>
            {tickets.map((ticket) => (
              <Box
                as="button"
                type="button"
                key={ticket._id}
                onClick={() => openTicket(ticket._id)}
                textAlign="left"
                bg={cardBg}
                borderWidth="1px"
                borderStyle="solid"
                borderColor={borderColor}
                borderRadius="12px"
                px={3}
                py={2}
                _hover={{ borderColor: headerBg }}
              >
                <Flex align="center" gap={2}>
                  <Text flex="1" fontSize="sm" fontWeight="semibold" noOfLines={1}>
                    {ticket.subject || "Support conversation"}
                  </Text>
                  {ticket.unread && (
                    <Badge bg="red.500" color="white" borderRadius="full" px={2}>
                      New
                    </Badge>
                  )}
                </Flex>
                {!!ticket.last_message_preview && (
                  <Text fontSize="xs" color={mutedColor} noOfLines={2}>
                    {ticket.last_message_preview}
                  </Text>
                )}
                <Text fontSize="11px" color={mutedColor} mt={1}>
                  {ticket.status === "ai" ? `${brand} assistant` : STATUS_LABELS[ticket.status] || ticket.status}
                  {ticket.last_message_at
                    ? ` · ${moment(ticket.last_message_at).fromNow()}`
                    : ""}
                </Text>
              </Box>
            ))}
            <Button
              size="sm"
              variant="ghost"
              alignSelf="flex-start"
              h="32px"
              onClick={startNewConversation}
            >
              New conversation
            </Button>
          </VStack>
        ) : (
          <>
            {sharedThread && (
              <Text fontSize="xs" color={mutedColor} mb={3} data-testid="support-thread-participants">
                {`Also on this conversation: ${colleagues.join(", ")}`}
              </Text>
            )}
            {!messages.length && (
              <Text fontSize="sm" color={mutedColor}>
                Hi{user?.name ? ` ${String(user.name).split(" ")[0]}` : ""}, how can we
                help? Ask a question and the assistant will answer right away — our
                team can jump in whenever you need them.
              </Text>
            )}
            {messages.map((message) => {
              // A customer message is the viewer's own unless the API says it is a
              // colleague's (`mine: false`) — a pending send is always theirs.
              const isUser = message.role === "user" && message.mine !== false;
              const colleague = message.role === "user" && message.mine === false;
              return (
                <Flex
                  key={message._id}
                  direction="column"
                  align={isUser ? "flex-end" : "flex-start"}
                  mb={3}
                >
                  {!isUser && (
                    <Text fontSize="11px" color={mutedColor} mb="2px">
                      {colleague
                        ? message.author_name || "A colleague"
                        : message.author_name || labels[message.role] || `${brand} support`}
                    </Text>
                  )}
                  {isUser && sharedThread && (
                    // Their own name too, so a colleague reading over the thread and the
                    // viewer see the same attribution.
                    <Text fontSize="11px" color={mutedColor} mb="2px">
                      {message.author_name ? `${message.author_name} (you)` : "You"}
                    </Text>
                  )}
                  {/* A message can be attachments only, and an empty bubble
                      under them would just look broken. */}
                  {!!String(message.body || "").trim() && (
                    <Box
                      maxW="85%"
                      px={3}
                      py={2}
                      bg={isUser ? userBubbleBg : agentBubbleBg}
                      color={isUser ? userBubbleColor : agentBubbleColor}
                      borderWidth={isUser ? 0 : "1px"}
                      borderStyle="solid"
                      borderColor={isUser ? "transparent" : borderColor}
                      borderRadius={isUser ? "16px 16px 0 16px" : "16px 16px 16px 0"}
                      opacity={message.pending ? 0.6 : 1}
                      fontSize="sm"
                      whiteSpace="pre-wrap"
                      wordBreak="break-word"
                      sx={{
                        "& p": { margin: 0 },
                        "& ul, & ol": { margin: "4px 0", paddingLeft: "18px" },
                        // The link colour the body used to carry inline. Applied here
                        // so the sanitizer can refuse `style` on everything.
                        "& a": {
                          color: isUser ? userLinkColor : agentLinkColor,
                          textDecoration: "underline",
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: sanitizedBody(
                          message.body,
                          // the assistant's prose is never linkified; its `links` are
                          { linkify: message.role !== "assistant" }
                        ),
                      }}
                    />
                  )}
                  {renderAttachments(message, isUser)}
                  {!isUser && !colleague && message.links?.length > 0 && (
                    <Flex wrap="wrap" gap={2} mt={2}>
                      {message.links.map((link) => (
                        <Button
                          key={link.path}
                          size="xs"
                          variant="outline"
                          borderRadius="full"
                          rightIcon={<ArrowForwardIcon />}
                          onClick={() => goTo(link.path)}
                        >
                          {link.label}
                        </Button>
                      ))}
                    </Flex>
                  )}
                  <Text fontSize="10px" color={mutedColor} mt="2px">
                    {message.pending
                      ? "Sending…"
                      : moment(message.created).format("MMM D, h:mm A")}
                  </Text>
                </Flex>
              );
            })}
            {showTyping && (
              <Flex align="center" gap={2} color={mutedColor}>
                <Spinner size="xs" />
                <Text fontSize="xs">{`${brand} assistant is typing…`}</Text>
              </Flex>
            )}
          </>
        )}
      </Box>

      {!showList && (
        <Box
          flexShrink={0}
          bg={panelBg}
          borderTopWidth="1px"
          borderTopStyle="solid"
          borderColor={borderColor}
          px={3}
          pt={3}
          pb={{ base: "calc(12px + env(safe-area-inset-bottom))", sm: "12px" }}
        >
          {showEscalate && (
            <Button
              size="sm"
              variant="ghost"
              h="32px"
              mb={2}
              onClick={escalate}
              isLoading={escalating}
              // Not while a message is still going out. The assistant may be answering
              // that very message, and an escalation landing underneath it makes its
              // answer lose the swap — which used to page the whole team a second time
              // for one message and one click. The server no longer double-pages either
              // way; this keeps the customer from being offered a choice whose outcome
              // depends on which request lands first.
              isDisabled={sending}
            >
              Talk to a human
            </Button>
          )}
          {attachments.length > 0 && (
            <Flex wrap="wrap" gap={2} mb={2}>
              {attachments.map((attachment) => (
                <Flex
                  key={attachment._id}
                  align="center"
                  gap={1}
                  minW={0}
                  maxW="100%"
                  bg={chipBg}
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor={borderColor}
                  borderRadius="full"
                  pl={2}
                  pr="2px"
                  py="2px"
                >
                  {attachment.uploading ? (
                    <Spinner size="xs" color={mutedColor} flexShrink={0} />
                  ) : (
                    <AttachmentIcon boxSize={3} color={mutedColor} flexShrink={0} />
                  )}
                  <Text fontSize="11px" noOfLines={1} maxW="120px">
                    {attachment.name}
                  </Text>
                  <Text fontSize="10px" color={mutedColor} flexShrink={0}>
                    {formatBytes(attachment.size)}
                  </Text>
                  <IconButton
                    aria-label={`Remove ${attachment.name}`}
                    icon={<SmallCloseIcon />}
                    // Not while it is still uploading. The upload cannot be called back:
                    // if S3 stores the object after the row is gone, nothing will ever
                    // reference it and nothing will remove it, leaving somebody's support
                    // file in private storage unreachable. It is a second or two, and the
                    // spinner says why.
                    isDisabled={!!attachment.uploading}
                    onClick={() => removeAttachment(attachment._id)}
                    variant="ghost"
                    size="xs"
                    minW="20px"
                    h="20px"
                    px={0}
                    borderRadius="full"
                  />
                </Flex>
              ))}
            </Flex>
          )}
          {!!attachError && (
            <Text fontSize="11px" color={errorColor} mb={2}>
              {attachError}
            </Text>
          )}
          <Flex align="flex-end" gap={2}>
            <Input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              hidden
              onChange={(e) => {
                const picked = Array.from(e.target.files || []);
                // Cleared so picking the same file again still fires onChange.
                e.target.value = "";
                attachFiles(picked);
              }}
            />
            <IconButton
              aria-label="Attach a file"
              icon={<AttachmentIcon />}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              isDisabled={sending || attachments.length >= MAX_ATTACHMENTS}
              variant="ghost"
              w="40px"
              h="40px"
              minW="40px"
              px={0}
              borderRadius="full"
            />
            <Textarea
              ref={inputRef}
              value={draft}
              // The server refuses anything longer rather than storing half of it, so
              // the limit belongs here too — finding out at send time that a long
              // paste will not go is worse than not being able to paste it.
              maxLength={MAX_MESSAGE_CHARS}
              onChange={(e) => setDraft(e.target.value.slice(0, MAX_MESSAGE_CHARS))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Ask us anything…"
              rows={1}
              resize="none"
              flex="1"
              minW={0}
              minH="40px"
              maxH="120px"
              fontSize="sm"
              borderRadius="12px"
              bg={inputBg}
              borderColor={borderColor}
            />
            <IconButton
              aria-label="Send message"
              icon={<ArrowUpIcon />}
              onClick={sendMessage}
              isDisabled={!canSend}
              isLoading={sending}
              w="40px"
              h="40px"
              minW="40px"
              px={0}
              borderRadius="full"
            />
          </Flex>
        </Box>
      )}
    </Flex>
  );
}

// The support widget is mounted in the app shell: a failure inside it must never
// take the rest of the app down with it.
class SupportWidgetBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("support widget crashed", error, info);
  }

  render() {
    if (this.state.hasError) return null;
    return <SupportWidget />;
  }
}

/**
 * Remounts the whole widget when the practice or the user changes.
 *
 * Clearing state in an effect is not enough on its own: the old tenant's
 * conversation can paint for one frame before the effect runs, in-flight flags
 * (sending, escalating) can be left stuck, and an A -> B -> A switch can let a
 * stale response from the first A overwrite newer state. A key makes all three
 * impossible, and resets the error boundary along with it.
 */
function SupportWidgetMount() {
  const { user, selectedBusiness } = useAppContext();
  return <SupportWidgetBoundary key={`${user?.id || ""}:${selectedBusiness?.bid || ""}`} />;
}

export default SupportWidgetMount;
